/**
 * Image helper utilities
 */

import { API_CONFIG } from '@/constants/api';

/**
 * Get product image URL
 * @param product - Object with a main_image field (e.g. Product, FilterProduct)
 * @param fallbackImage - Fallback image path (default: "/assets/products/2007-228x228.webp")
 * @returns Image URL
 */
export function getProductImageUrl(
  product: { main_image?: string },
  fallbackImage: string = '/assets/products/2007-228x228.webp'
): string {
  if (!product.main_image) {
    return fallbackImage;
  }

  // If it's a full URL, use it directly
  if (product.main_image.startsWith('http')) {
    return product.main_image;
  }

  // Otherwise construct the URL
  return `${API_CONFIG.BASE_URL}/images/products/${product.main_image}`;
}

/**
 * Handle image error by setting fallback
 * @param event - Image error event
 * @param fallbackImage - Fallback image path
 */
export function handleImageError(
  event: React.SyntheticEvent<HTMLImageElement>,
  fallbackImage: string = '/assets/products/2007-228x228.webp'
): void {
  event.currentTarget.src = fallbackImage;
}

/**
 * Build generic image URL from API server
 * @param imagePath - Relative image path (e.g., "MeasureFilter/measurefilter1.png")
 * @returns Full image URL
 */
export function buildImageUrl(imagePath: string): string {
  // If it's already a full URL, return it
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // If it starts with /, it's from the public folder
  if (imagePath.startsWith('/')) {
    return imagePath;
  }

  // Otherwise construct the URL from API server
  return `${API_CONFIG.BASE_URL}/images/${imagePath}`;
}

/**
 * Preload critical images
 * @param imageUrls - Array of image URLs to preload
 */
export function preloadImages(imageUrls: string[]): void {
  imageUrls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
}
